import { Link } from 'react-router-dom';
import { categoriaDe, dataDoFeed, situacaoPrazo, textoPrazo, cienciaCobradaDe, cienciaEmDia } from '../publicacao.definitions';
import type { MeuEstadoPublicacao, Publicacao } from '../publicacao.types';
import { PublicacaoCapa } from './PublicacaoCapa';

interface PublicacaoCardProps {
  publicacao: Publicacao;
  estado: MeuEstadoPublicacao | undefined;
  fixada: boolean;
  colaboradorId: string | null | undefined;
  agora: number;
  to: string;
}

function selo(props: PublicacaoCardProps): { texto: string; classe: string } | null {
  const { publicacao, estado, colaboradorId, agora } = props;
  if (cienciaCobradaDe(publicacao, colaboradorId) && !cienciaEmDia(publicacao, estado)) {
    const situacao = situacaoPrazo(publicacao, agora);
    if (situacao.tipo === 'atrasada') return { texto: textoPrazo(situacao), classe: 'bg-rose-100 text-rose-700' };
    if (situacao.tipo === 'vence') {
      const ate = new Date(situacao.em).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
      return { texto: `Confirmar ciência até ${ate}`, classe: 'bg-amber-100 text-amber-800' };
    }
    return { texto: 'Confirmar ciência', classe: 'bg-amber-100 text-amber-800' };
  }
  if (!estado) return null;
  return estado.lida ? { texto: 'Lida ✓', classe: 'text-raro-gray-400' } : { texto: 'Não lida', classe: 'bg-primary-50 text-primary-700' };
}

export function PublicacaoCard(props: PublicacaoCardProps) {
  const { publicacao, fixada, to } = props;
  const categoria = categoriaDe(publicacao);
  const documento = publicacao.tipo === 'documento';
  const novaVersao = documento && (publicacao.versao ?? 1) > 1;
  const s = selo(props);
  const data = dataDoFeed(publicacao);
  return (
    <Link
      to={to}
      className="flex flex-col overflow-hidden rounded-2xl border border-raro-gray-200 bg-white shadow-sm transition hover:-translate-y-0.5 hover:border-primary-200 hover:shadow-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-500"
    >
      <PublicacaoCapa
        titulo={documento ? `${novaVersao ? 'Nova versão: ' : '📄 '}${publicacao.titulo}` : publicacao.titulo}
        categoria={publicacao.categoria}
        tipo={publicacao.tipo}
        capaUrl={publicacao.capaUrl}
      />
      <div className="flex flex-1 flex-col gap-1.5 p-5">
        <div className="flex items-center justify-between gap-2">
          <span className={`text-xs font-semibold ${categoria.texto}`}>
            {fixada && '📌 Fixada · '}
            {documento && `Documento${novaVersao ? ` v${publicacao.versao}` : ''} · `}
            {categoria.label}
            {publicacao.documentoRelacionadoId && <span className="ml-1 font-normal text-raro-gray-500">· 📄 com documento</span>}
          </span>
          {s && <span className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-semibold ${s.classe}`}>{s.texto}</span>}
        </div>
        <h3 className="font-montserrat text-base font-semibold text-raro-gray-900">{publicacao.titulo}</h3>
        <p className="text-sm text-raro-gray-500">{novaVersao && publicacao.oQueMudou ? publicacao.oQueMudou : publicacao.resumo}</p>
        <div className="mt-auto flex items-center justify-between border-t border-dashed border-primary-200 pt-3 text-xs text-raro-gray-500">
          <span>
            {data ? new Date(data).toLocaleDateString('pt-BR') : ''} · {publicacao.autor?.name ?? 'Time de SI'}
          </span>
          {!documento && (
            <span>
              ♥ {publicacao.curtidasTotal ?? 0}
              {(publicacao.comentariosTotal ?? 0) > 0 && ` · 💬 ${publicacao.comentariosTotal}`}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}
